import { z } from 'zod';
import { ROLES } from '../permissions.js';
import { gstin, gstRate, optionalPhone, percent, phone, pincode, rupees, shortText, } from './common.js';
export const roleEnum = z.enum(ROLES);
/**
 * Six digits from an authenticator, or one of the recovery codes.
 *
 * Deliberately loose: spacing and case are normalised on the server, and a
 * wrong shape has to fail as a wrong code, not as a validation error that
 * tells somebody which of the two they typed.
 */
export const totpCode = z.string().trim().min(6).max(12);
/** A Google reCAPTCHA response token. Long, opaque, and validated by Google. */
export const captchaToken = z.string().trim().min(1).max(4096);
export const loginSchema = z.object({
    email: z.string().trim().toLowerCase().email('Enter a valid email address'),
    password: z.string().min(1, 'Enter your password'),
    propertyId: z.string().uuid().optional(),
    /**
     * The calling device's persisted id (posData's persistedDeviceId()), sent
     * only by the Android kiosk build. A device an admin has registered/pinned
     * to a property (Phase 6 terminal registration) resolves against that
     * property regardless of propertyId above, and a request naming a
     * different one is rejected - see resolvePinnedPropertyId in
     * modules/auth/service.ts. Absent entirely for ordinary browser sign-ins,
     * which carry no device-pinning concept at all.
     */
    deviceId: z.string().uuid().optional(),
    /** Challenge id and answer, when the server is asking for one. */
    captchaId: z.string().uuid().optional(),
    captchaAnswer: z.string().trim().min(1).max(12).optional(),
    /**
     * The reCAPTCHA token, when a superadmin has configured Google's challenge.
     *
     * A field of its own rather than reusing captchaAnswer: these tokens run to
     * a couple of thousand characters, and widening the answer field to fit one
     * would stop it rejecting a five-character answer that is obviously wrong.
     */
    captchaToken: captchaToken.optional(),
    /**
     * The six digits from the authenticator, or a recovery code.
     *
     * Sent on the second attempt: the first is answered with TOTP_REQUIRED,
     * because whether an account has two-step verification on is not something
     * to reveal before the password has been checked.
     */
    totp: totpCode.optional(),
    /**
     * "Remember me". True (or absent) keeps this device signed in for the full
     * refresh-token lifetime; false makes the session end when the browser or
     * app is closed, and in any case after a few hours.
     */
    rememberMe: z.boolean().optional(),
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: z
        .string()
        .min(12, 'Use at least 12 characters')
        .max(128)
        .refine((v) => /[a-z]/.test(v), 'Include a lowercase letter')
        .refine((v) => /[A-Z]/.test(v), 'Include an uppercase letter')
        .refine((v) => /[0-9]/.test(v), 'Include a number'),
});
/* ---------------- two-step verification ---------------- */
/** Turning it on: the code proves the phone really holds the same secret. */
export const enableTwoFactorSchema = z.object({ code: totpCode });
/**
 * Turning it off asks for the password again.
 *
 * Somebody who walks up to an unlocked terminal should not be able to strip the
 * second factor off the account in two clicks.
 */
export const disableTwoFactorSchema = z.object({
    password: z.string().min(1, 'Enter your password'),
});
export const createUserSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    name: shortText,
    password: changePasswordSchema.shape.newPassword,
    phone: optionalPhone,
    memberships: z
        .array(z.object({ propertyId: z.string().uuid(), role: roleEnum }))
        .min(1, 'Grant access to at least one property'),
});
export const updateUserSchema = z.object({
    name: shortText.optional(),
    phone: optionalPhone,
    isActive: z.boolean().optional(),
    memberships: z.array(z.object({ propertyId: z.string().uuid(), role: roleEnum })).optional(),
});
export const resetPasswordSchema = z.object({
    userId: z.string().uuid(),
    newPassword: changePasswordSchema.shape.newPassword,
});
export const createPropertySchema = z.object({
    name: shortText,
    code: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z0-9-]{2,12}$/, 'Use 2-12 letters, digits or hyphens'),
    legalName: shortText.optional(),
    gstin,
    stateCode: z.string().regex(/^\d{2}$/, 'Two-digit GST state code'),
    address: z.string().trim().min(1).max(240),
    city: shortText,
    pincode,
    phone,
    email: z.string().trim().toLowerCase().email('Enter the hotel’s email address'),
    fssai: z.string().trim().max(20).optional().or(z.literal('')),
    currency: z.string().length(3).default('INR'),
    timezone: z.string().min(3).max(48).default('Asia/Kolkata'),
});
/* currency and timezone re-declared without defaults: Zod 4 applies .default()
   inside .partial(), so any property edit reset them to INR and Asia/Kolkata. */
export const updatePropertySchema = createPropertySchema
    .partial()
    .omit({ code: true })
    .extend({
    currency: z.string().length(3).optional(),
    timezone: z.string().min(3).max(48).optional(),
});
export const forgotPasswordSchema = z.object({
    email: z.string().trim().toLowerCase().email('Enter the email you sign in with'),
});
export const resetWithTokenSchema = z.object({
    token: z.string().trim().min(16, 'That reset link is not valid'),
    newPassword: changePasswordSchema.shape.newPassword,
});
export const propertySettingsSchema = z.object({
    targetFoodCostPct: percent.optional(),
    /**
     * Added to a table's bill, if an owner sets one.
     *
     * Zero by default and left that way: Indian consumer guidance is that a
     * service charge must not be added to a food bill automatically.
     */
    serviceChargePct: percent.optional(),
    invoicePrefix: z.string().trim().max(8).optional(),
    /** What a takeaway box and a delivery run cost the guest, in rupees. */
    charges: z
        .object({
        packagingTakeaway: rupees,
        packagingDelivery: rupees,
        deliveryFee: rupees,
    })
        .partial()
        .optional(),
    payroll: z
        .object({
        basicPct: percent,
        hraPct: percent,
        pfPct: percent,
        pfCeiling: rupees,
        esiPct: percent,
        esiCeiling: rupees,
        ptAmount: rupees,
        ptThreshold: rupees,
        otMultiplier: z.number().min(1).max(3),
        advanceRecoveryCapPct: percent,
        standardHoursPerDay: z.number().min(1).max(16),
    })
        .partial()
        .optional(),
    /** Aggregator commission, keyed by channel: { SWIGGY: 22, ZOMATO: 20 }. */
    channelCommission: z.record(z.string().trim().max(20), percent).optional(),
    /**
     * GST slabs and service codes, so a council notification does not need a
     * release. The defaults are the rates that used to be compiled in.
     */
    tax: z
        .object({
        restaurantSac: z.string().trim().min(4).max(10),
        accommodationSac: z.string().trim().min(4).max(10),
        /** Declared tariff per room per night, in rupees, at or below which the lower slab applies. */
        roomTariffThreshold: rupees,
        roomGstBelow: gstRate,
        roomGstAbove: gstRate,
    })
        .partial()
        .optional(),
    security: z
        .object({
        /**
         * Minutes of inactivity before a session is signed out. 0 means never,
         * which is what every property had before this setting existed.
         *
         * The ceiling is a day: a terminal on the front desk that stays signed in
         * for a week is the thing this setting exists to prevent.
         */
        idleTimeoutMinutes: z
            .number()
            .int()
            .min(0, 'Use 0 to switch the idle timeout off')
            .max(1440, 'A day is the longest this can be'),
        /**
         * Hours a Phase 8 offline capability grant remains valid for once
         * issued (ADR-009). 0 disables browser/kiosk offline sign-in for this
         * property outright, mirroring `idleTimeoutMinutes: 0`'s existing
         * "off" convention. Default proposal in ADR-009 is 72.
         */
        offlineGrantCeilingHours: z
            .number()
            .int()
            .min(0, 'Use 0 to switch offline sign-in off')
            .max(24 * 14, 'Two weeks is the longest an offline grant may run unrenewed'),
        /** Days a till keeps synced bills on the device before pruning them. */
        offlineRetentionDays: z
            .number()
            .int()
            .min(1, 'Keep at least a day')
            .max(90, 'Ninety days is the longest a device may keep bills'),
        /** Days a till may go without reaching the server before it locks offline sign-in. */
        offlineLockDays: z
            .number()
            .int()
            .min(1, 'Allow at least a day')
            .max(30, 'Thirty days is the longest a device may go without reaching the server'),
        /**
         * Whether staff need their Android device registered here before the app
         * lets them in. Off means online use without registering; the owner is
         * exempt either way.
         */
        requireDeviceRegistration: z.boolean(),
    })
        .partial()
        .optional(),
    /**
     * How much of a guest's ID document number is kept at check-in. ENCRYPTED
     * (the default) keeps it encrypted; LAST4_ONLY and NONE keep less.
     */
    privacy: z
        .object({
        guestIdStorage: z.enum(['ENCRYPTED', 'LAST4_ONLY', 'NONE']),
        /** Days before a data request shows as overdue; null for no due date. */
        requestResponseDays: z.number().int().min(1).max(365).nullable(),
    })
        .partial()
        .optional(),
});
//# sourceMappingURL=identity.js.map